import React, { useCallback } from 'react';
import { Filter } from './FilterBar';

type GetOptions = (labelAll: string) => { text: string; value: string }[];

type GetFiltersProps = {
  components: {
    getOptions?: GetOptions;
    component?: React.ReactNode;
    label: string;
    field: string;
    disabled?: boolean;
    multiple?: boolean;
    cssClass?: string;
    type?: 'searchable' | 'select';
    interfaceOptions?: any;
    includeAllOption?: boolean;
    filterLabelAll?: string;
    searchPlaceholder?: string;
    showTooltip?: boolean;
    tooltipText?: string;
    tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
    tooltipAlignment?: 'start' | 'center' | 'end';
    virtualized?: boolean;
  }[];
  handleFilters: (filter: string, value: string | number) => void;
  values: Record<string, string | number | undefined>;
};

const VALUE_ALL = 'all';

export const getFilters = ({ components, handleFilters, values }: GetFiltersProps): Filter[] => {
  return components.map(({
    label,
    component,
    getOptions,
    field,
    disabled,
    cssClass,
    multiple = false,
    interfaceOptions,
    type = 'select',
    includeAllOption = false,
    filterLabelAll,
    searchPlaceholder,
    showTooltip,
    tooltipText,
    tooltipSide,
    tooltipAlignment,
    virtualized,
  }) => ({
    label,
    getOptions,
    component,
    handleChange: (e: any) =>
      handleFilters(field, Array.isArray(e.detail.value) ? e.detail.value : String(e.detail.value)),
    selected: values?.[field] || 'all',
    disabled,
    multiple,
    cssClass,
    type,
    interfaceOptions,
    includeAllOption,
    filterLabelAll,
    searchPlaceholder,
    showTooltip,
    tooltipText,
    tooltipSide,
    tooltipAlignment,
    virtualized,
  }));
};

interface mapCustomArrayProps {
  data: Record<string, string>[];
  selector: (item: Record<string, string>) => { value: string; text: string };
  labelAll: string;
}

export const mapCustomArray = ({ data = [], selector, labelAll }: mapCustomArrayProps) =>
  useCallback(() => {
    const options = [{ value: VALUE_ALL, text: labelAll }];

    data.forEach((item: Record<string, string>) => {
      options.push(selector(item));
    });
    return options;
  }, [data]);

interface mapCustomObjectProps {
  data: Record<string, string>;
  labelAll: string | null;
}

export const mapCustomObject = ({ data = {}, labelAll }: mapCustomObjectProps) =>
  useCallback(() => {
    const options = labelAll ? [{ value: VALUE_ALL, text: labelAll }] : [];
    Object.keys(data).forEach((value) => {
      const text = data[value];
      options.push({
        value,
        text,
      });
    });
    return options;
  }, []);
